import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import vendorConfig from '../vendors/vendorConfig';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4003/api';
const STORAGE_KEY = `${vendorConfig.vendorId}-theme`;

// Base theme definitions
const allThemes = {
  light: { id: 'light', name: 'Light', description: 'Clean and bright', thumbnail: '/themes/light.png' },
  dark: { id: 'dark', name: 'Dark', description: 'Easy on the eyes', thumbnail: '/themes/dark.png' },
  solana: { id: 'solana', name: 'Solana', description: 'High performance blockchain', thumbnail: '/themes/solana.png' },
  ethereum: { id: 'ethereum', name: 'Ethereum', description: 'Decentralized platform', thumbnail: '/themes/ethereum.png' },
  bitcoin: { id: 'bitcoin', name: 'Bitcoin', description: 'Digital gold standard', thumbnail: '/themes/bitcoin.png' },
  base: { id: 'base', name: 'Base', description: 'Coinbase L2 network', thumbnail: '/themes/base.png' },
};

// Build available themes list from vendor config
function buildThemes() {
  const available = vendorConfig.theme.availableThemes;
  const result = {};
  for (const themeId of available) {
    if (allThemes[themeId]) {
      result[themeId] = allThemes[themeId];
    } else if (vendorConfig.theme.overrides[themeId]) {
      const override = vendorConfig.theme.overrides[themeId];
      result[themeId] = {
        id: themeId,
        name: override.name || themeId,
        description: override.description || '',
        thumbnail: override.thumbnail || null,
      };
    }
  }
  return result;
}

// Determine the base CSS theme for a vendor custom theme
// e.g. "acme-light" → "light", "acme-dark" → "dark"
function getBaseTheme(themeId) {
  if (allThemes[themeId]) return themeId;
  // Check if it ends with a base theme name
  for (const base of Object.keys(allThemes)) {
    if (themeId.endsWith(`-${base}`)) return base;
  }
  return 'light'; // fallback
}

export const themes = buildThemes();

const ThemeContext = createContext(null);

export function ThemeProvider({ children, user }) {
  const defaultTheme = vendorConfig.theme.defaultTheme;
  const [theme, setTheme] = useState(defaultTheme);
  const [isLoading, setIsLoading] = useState(true);
  const appliedOverridesRef = useRef([]);

  // Clear any previously applied vendor CSS overrides
  const clearOverrides = useCallback(() => {
    for (const varName of appliedOverridesRef.current) {
      document.documentElement.style.removeProperty(varName);
    }
    appliedOverridesRef.current = [];
  }, []);

  // Apply theme to document
  const applyTheme = useCallback((themeName) => {
    if (!themes[themeName]) return;

    // Set the base CSS theme
    const baseTheme = getBaseTheme(themeName);
    document.documentElement.setAttribute('data-theme', baseTheme);

    // Clear previous vendor overrides
    clearOverrides();

    // Apply vendor-specific CSS variable overrides with important priority
    const overrides = vendorConfig.theme.overrides[themeName]?.variables;
    if (overrides) {
      const keys = [];
      for (const [varName, value] of Object.entries(overrides)) {
        document.documentElement.style.setProperty(varName, value, 'important');
        keys.push(varName);
      }
      appliedOverridesRef.current = keys;
    }
  }, [clearOverrides]);

  // Load theme from localStorage on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem(STORAGE_KEY);
    if (savedTheme && themes[savedTheme]) {
      setTheme(savedTheme);
      applyTheme(savedTheme);
    } else {
      applyTheme(defaultTheme);
      localStorage.setItem(STORAGE_KEY, defaultTheme);
    }
    setIsLoading(false);
  }, [applyTheme, defaultTheme]);

  // Sync with database if user is logged in
  useEffect(() => {
    if (user) {
      fetchUserTheme();
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUserTheme = async () => {
    try {
      const localTheme = localStorage.getItem(STORAGE_KEY);
      if (localTheme && themes[localTheme]) {
        return; // Already have a valid local theme
      }

      const response = await fetch(`${API_URL}/user/preferences`, {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success && data.data.theme && themes[data.data.theme]) {
        const dbTheme = data.data.theme;
        if (dbTheme !== theme) {
          setTheme(dbTheme);
          applyTheme(dbTheme);
          localStorage.setItem(STORAGE_KEY, dbTheme);
        }
      }
    } catch (error) {
      console.error('Failed to fetch user theme:', error);
    }
  };

  const saveThemeToDatabase = async (newTheme) => {
    try {
      await fetch(`${API_URL}/user/preferences/theme`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ theme: newTheme })
      });
    } catch (error) {
      console.error('Failed to save theme to database:', error);
    }
  };

  const changeTheme = useCallback((newTheme) => {
    if (!themes[newTheme]) return;

    setTheme(newTheme);
    applyTheme(newTheme);
    localStorage.setItem(STORAGE_KEY, newTheme);

    if (user) {
      saveThemeToDatabase(newTheme);
    }
  }, [user, applyTheme]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentTheme = themes[theme] || themes[defaultTheme] || Object.values(themes)[0];

  const value = {
    theme,
    themes,
    currentTheme,
    changeTheme,
    isLoading
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export default ThemeContext;
